// Public copy based on the 21 September 2026 product assessment.
// These are bounded development-build capabilities, not a release offer.
export const features = [
  { title: "Pupil records in context", desc: "Browse pupil records alongside their class, section, campus and enrolment information." },
  { title: "Daily attendance entries", desc: "Record present or absent entries for a selected section and date, with last-recorder details and timestamps for saved entries." },
  { title: "Fee and payment records", desc: "Record charges and received payments, link payments to charges, and view printable payment acknowledgements. Recording a payment is not online payment collection or bank reconciliation." },
  { title: "Financial correction history", desc: "Correct supported financial entries while retaining earlier entries and their linked replacements. Attendance and mark updates do not retain this same complete previous-value history." },
  { title: "School assessment marks", desc: "Record and review school-stated assessment marks by pupil, subject and date. This is not a complete examination-management system." },
];

export const developmentStatus = [
  {
    status: "Implemented core",
    title: "Bounded school-record workflows",
    desc: "The evidenced development build includes pupil records, attendance entries, fee and payment records, supported financial corrections, payment acknowledgements and assessment marks. These are not a completed all-in-one school ERP.",
  },
  {
    status: "Separate development candidate",
    title: "Extended admissions enquiries",
    desc: "A separately prepared candidate contains admissions enquiries, contact handling, stages and follow-ups. It is not established as installed or available to schools; broader validation remains outstanding.",
  },
  {
    status: "Not generally available",
    title: "Development and validation continue",
    desc: "Selected workflows have been tested using synthetic records. Broader validation and release approval remain outstanding. No public self-service demo, pilot programme or general onboarding offer is established.",
  },
];

export const recordControls = [
  {
    title: "Staff, school and campus access",
    desc: "The development build includes staff sign-in and school/campus-scoped access checks. These mechanisms are not a security certification or a guarantee covering every workflow.",
  },
  {
    title: "Financial history, with a defined scope",
    desc: "Supported financial corrections retain earlier entries and linked replacements. Attendance stores the last recorder and timestamp; attendance and assessment updates do not retain a complete previous-value history. This is not a complete audit trail for every record.",
  },
];

export const quillFaq = [
  { q: "Who is Quill for?", a: "Quill is being developed for school staff working with pupil records, daily attendance, assessment marks and fee entries. It is not a public parent or pupil application." },
  { q: "Can our school start using Quill?", a: "Quill is in development, not a generally available service. Development and validation are continuing. Contact KedByte to discuss your requirements; an enquiry does not create an account or reserve access." },
  { q: "Does every record retain its full history?", a: "No. Supported financial corrections retain earlier entries and linked replacements. Attendance entries record the last recorder and timestamp; attendance and assessment updates do not preserve a complete previous-value chain." },
  { q: "Does Quill collect online payments or reconcile bank transactions?", a: "The evidenced development build records charges and received payments, links payments to charges, and renders printable payment acknowledgements. This does not establish online payment collection, bank integration, bank reconciliation or official numbered receipt issuance." },
  { q: "Is there a parent or pupil portal?", a: "The evidenced audience is school staff. A generally available parent application, pupil portal or mobile product is not established, and no delivery date is promised here." },
];

export const QUILL_CTA = {
  // Compatibility contract: keep existing stored lead sources/integrations.
  // This internal legacy value is not the public availability offer.
  source: "schoolos-early-access",
  eyebrow: "Quill enquiry",
  title: "Discuss Quill with KedByte",
  subtitle: "Tell us which school-recordkeeping workflow you would like to discuss. Please do not send pupil records, credentials or financial data through this enquiry.",
  messageLabel: "Your Quill requirements",
  messagePlaceholder: "Which school-recordkeeping workflow would you like to discuss?",
};

export const quillSchema = [
  {
    "@type": "SoftwareApplication",
    name: "Quill",
    url: "https://kedbyte.com/quill",
    applicationCategory: "EducationalApplication",
    operatingSystem: "Web",
    description: "School records software in development for staff working with pupil records, attendance, assessment marks and fee entries. Not a generally available service.",
    author: { "@type": "Organization", "@id": "https://kedbyte.com/#organization", name: "KedByte Technologies Private Limited" },
  },
  {
    "@type": "FAQPage",
    mainEntity: quillFaq.map(({ q, a }) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  },
];
